import { useDataExplorerAppearance } from './appearanceContext';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import type { MouseEvent } from 'react';
import { useState } from 'react';

import type {
  DataExplorerSplitButtonItem,
  DataExplorerSplitButtonProps,
} from './types';
import { getDataExplorerActionToneClassName, resolveDataExplorerAppearance } from './theme';
import { cn } from './utils';
import { DataExplorerButton } from './DataExplorerButton';

function ChevronDownIcon({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={cn('h-4 w-4 transition-transform', open && 'rotate-180')}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export function DataExplorerSplitButton({
  label,
  icon,
  items = [],
  onPrimaryAction,
  onAction,
  menuLabel,
  triggerLabel = 'More actions',
  title,
  tone,
  active = false,
  disabled = false,
  appearance,
  className,
  classNames,
}: DataExplorerSplitButtonProps) {
  const [open, setOpen] = useState(false);
  const theme = resolveDataExplorerAppearance(
    useDataExplorerAppearance(appearance),
  );
  const hasItems = items.length > 0;
  const resolvedActive = active || open;

  const handlePrimaryClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    onPrimaryAction?.(event);
  };

  return (
    <DropdownMenu.Root open={hasItems && open} onOpenChange={(nextOpen) => setOpen(hasItems ? nextOpen : false)}>
      <div
        className={cn(
          'inline-flex min-w-0 shrink-0 overflow-hidden rounded-md',
          getDataExplorerActionToneClassName(theme, tone, resolvedActive),
          classNames?.root,
          className,
        )}
      >
        <DataExplorerButton
          label={label}
          icon={icon}
          title={title}
          disabled={disabled}
          active={resolvedActive}
          appearance={appearance}
          className={cn('rounded-none border-r-0', classNames?.primary)}
          onClick={handlePrimaryClick}
        />
        {hasItems ? (
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              aria-label={triggerLabel}
              title={triggerLabel}
              disabled={disabled}
              className={cn(
                theme.slots.controlButton,
                'w-9 shrink-0 rounded-none border-l-0 px-0',
                resolvedActive
                  ? getDataExplorerActionToneClassName(theme, tone, true)
                  : getDataExplorerActionToneClassName(theme, tone, false),
                classNames?.trigger,
              )}
              onClick={(event) => event.stopPropagation()}
            >
              <ChevronDownIcon open={open} />
            </button>
          </DropdownMenu.Trigger>
        ) : null}
      </div>
      {hasItems ? (
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="start"
            sideOffset={6}
            className={cn(theme.slots.menuContent, classNames?.content)}
            style={theme.styles.menuContent}
          >
            {menuLabel ? (
              <>
                <DropdownMenu.Label className={theme.slots.menuLabel}>{menuLabel}</DropdownMenu.Label>
                <DropdownMenu.Separator className={theme.slots.menuSeparator} />
              </>
            ) : null}
            {items.map((item: DataExplorerSplitButtonItem) => (
              <DropdownMenu.Item
                key={item.id}
                title={item.title}
                disabled={item.disabled}
                className={cn(
                  theme.slots.menuItem,
                  getDataExplorerActionToneClassName(theme, item.tone, false),
                  classNames?.item,
                )}
                style={theme.styles.menuItem}
                onSelect={() => {
                  if (!item.disabled) onAction?.(item.id);
                }}
              >
                {item.icon ? <span className={theme.slots.menuItemIcon}>{item.icon}</span> : null}
                <span className={theme.slots.menuItemLabel}>{item.label}</span>
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      ) : null}
    </DropdownMenu.Root>
  );
}
